/**
 * World Loader
 * Loads demo worlds into the A-Frame scene
 */

import { DemoWorlds } from './demo-worlds.js';

export class WorldLoader {
    constructor(scene) {
        this.scene = scene;
        this.currentWorld = null;
        this.objectContainer = null;
    }

    /**
     * Initialize the world loader
     */
    init() {
        // Get or create container for dynamic objects
        this.objectContainer = this.scene.querySelector('#dynamic-content');
        if (!this.objectContainer) {
            this.objectContainer = document.createElement('a-entity');
            this.objectContainer.setAttribute('id', 'dynamic-content');
            this.scene.appendChild(this.objectContainer);
        }

        console.log('🌍 WorldLoader initialized');
    }

    /**
     * Load a random demo world
     */
    loadRandomWorld() {
        const world = DemoWorlds.getRandomWorld();
        console.log(`🎲 Loading random world: ${world.name}`);
        this.loadWorld(world);
        return world;
    }

    /**
     * Load a specific world
     */
    loadWorld(world) {
        console.log(`🌍 Loading world: ${world.name}`);
        this.currentWorld = world;

        // Clear existing world
        this.clearWorld();

        // Update environment
        this.updateEnvironment(world.environment);

        // Create objects
        this.createObjects(world.objects);

        // Update lights
        this.updateLights(world.lights);

        console.log(`✅ World loaded: ${world.name}`);

        // Show world info to user
        this.showWorldInfo(world);
    }

    /**
     * Clear current world
     */
    clearWorld() {
        if (this.objectContainer) {
            while (this.objectContainer.firstChild) {
                this.objectContainer.removeChild(this.objectContainer.firstChild);
            }
        }
    }

    /**
     * Update environment settings
     */
    updateEnvironment(env) {
        const environment = this.scene.querySelector('#environment');
        if (environment) {
            const envString = Object.keys(env)
                .map(key => {
                    if (typeof env[key] === 'object' && env[key] !== null) {
                        const { x, y, z } = env[key];
                        return `${key}: ${x} ${y} ${z}`;
                    }
                    return `${key}: ${env[key]}`;
                })
                .join('; ');
            
            environment.setAttribute('environment', envString);
        }

        // Update sky if provided
        if (env.skyColor) {
            const sky = this.scene.querySelector('#sky, a-sky');
            if (sky) {
                sky.setAttribute('color', env.skyColor);
            }
        }
    }

    /**
     * Create objects in the scene
     */
    createObjects(objects) {
        if (!objects || objects.length === 0) return;

        objects.forEach((obj, index) => {
            const entity = this.createObject(obj);
            entity.setAttribute('class', 'clickable dynamic-object');
            entity.setAttribute('data-object-id', index);
            this.objectContainer.appendChild(entity);
        });
    }

    /**
     * Create a single object
     */
    createObject(obj) {
        const entity = document.createElement('a-entity');
        
        // Determine primitive type
        const primitiveType = obj.type || 'box';
        const primitive = document.createElement(`a-${primitiveType}`);

        // Set position
        if (obj.position) {
            primitive.setAttribute('position', `${obj.position.x} ${obj.position.y} ${obj.position.z}`);
        }

        // Set scale
        if (obj.scale) {
            primitive.setAttribute('scale', `${obj.scale.x} ${obj.scale.y} ${obj.scale.z}`);
        }

        // Set rotation
        if (obj.rotation) {
            primitive.setAttribute('rotation', `${obj.rotation.x || 0} ${obj.rotation.y || 0} ${obj.rotation.z || 0}`);
        }

        // Set color
        if (obj.color) {
            primitive.setAttribute('color', obj.color);
        }

        // Set opacity
        if (obj.opacity !== undefined) {
            primitive.setAttribute('material', `opacity: ${obj.opacity}; transparent: true`);
        }

        // Add shadow
        primitive.setAttribute('shadow', 'cast: true; receive: true');

        // Add metalness and roughness for better visuals
        if (!obj.opacity) {
            primitive.setAttribute('material', 'metalness: 0.3; roughness: 0.7');
        }

        // Add animation if specified
        if (obj.animation) {
            this.addAnimation(primitive, obj.animation);
        }

        entity.appendChild(primitive);
        return entity;
    }

    /**
     * Add animation to object
     */
    addAnimation(element, animationType) {
        switch (animationType) {
            case 'rotate':
                element.setAttribute('animation', {
                    property: 'rotation',
                    to: '0 360 0',
                    loop: true,
                    dur: 10000,
                    easing: 'linear'
                });
                break;
            
            case 'float':
                element.setAttribute('animation', {
                    property: 'position',
                    dir: 'alternate',
                    from: element.getAttribute('position'),
                    to: this.modifyPosition(element.getAttribute('position'), 0, 0.5, 0),
                    loop: true,
                    dur: 2000,
                    easing: 'easeInOutSine'
                });
                break;
            
            case 'pulse':
                element.setAttribute('animation', {
                    property: 'scale',
                    dir: 'alternate',
                    from: element.getAttribute('scale') || '1 1 1',
                    to: this.modifyScale(element.getAttribute('scale') || '1 1 1', 1.2),
                    loop: true,
                    dur: 1500,
                    easing: 'easeInOutQuad'
                });
                break;
        }
    }

    /**
     * Modify position string
     */
    modifyPosition(posStr, dx, dy, dz) {
        const parts = posStr.split(' ').map(Number);
        return `${parts[0] + dx} ${parts[1] + dy} ${parts[2] + dz}`;
    }

    /**
     * Modify scale string
     */
    modifyScale(scaleStr, multiplier) {
        const parts = scaleStr.split(' ').map(Number);
        return `${parts[0] * multiplier} ${parts[1] * multiplier} ${parts[2] * multiplier}`;
    }

    /**
     * Update lighting
     */
    updateLights(lights) {
        if (!lights || lights.length === 0) return;

        const lightingRig = this.scene.querySelector('#lighting-rig');
        if (!lightingRig) return;

        // Clear existing dynamic lights
        const dynamicLights = lightingRig.querySelectorAll('.dynamic-light');
        dynamicLights.forEach(light => light.remove());

        // Add new lights
        lights.forEach(lightConfig => {
            const light = document.createElement('a-light');
            light.setAttribute('class', 'dynamic-light');
            light.setAttribute('type', lightConfig.type);
            
            if (lightConfig.position) {
                light.setAttribute('position', `${lightConfig.position.x} ${lightConfig.position.y} ${lightConfig.position.z}`);
            }
            
            if (lightConfig.color) {
                light.setAttribute('color', lightConfig.color);
            }
            
            if (lightConfig.intensity !== undefined) {
                light.setAttribute('intensity', lightConfig.intensity);
            }

            lightingRig.appendChild(light);
        });
    }

    /**
     * Show world info to user
     */
    showWorldInfo(world) {
        // Create info panel
        const infoEl = document.getElementById('world-info');
        if (infoEl) {
            infoEl.innerHTML = `
                <h3>${world.name}</h3>
                <p>${world.description}</p>
            `;
            infoEl.style.display = 'block';
            
            // Hide after 5 seconds
            setTimeout(() => {
                infoEl.style.display = 'none';
            }, 5000);
        }

        // Log to console
        console.log(`📍 World: ${world.name} - ${world.description}`);
    }

    /**
     * Get current world
     */
    getCurrentWorld() {
        return this.currentWorld;
    }
}
